import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { Form, Radio, DatePicker, Typography } from 'antd';

import BillingReportIcon from 'assets/reporting/BillingReport.svg?react';

import { RibbonButton, StyledModal } from 'common/components';

import userSelectors from '@redux/user/selectors';

import { useCheckIsSuperAdmin } from 'hooks';

import { useBillingReportActions } from './useBillingReport';

import { formatRangeDateTime } from 'utils/formatDate';

import './BillingReport.less';

const BillingReportControl = () => {
  const [visible, setVisible] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (location.pathname.includes('/reportings/billing')) {
      setVisible(true);
    }
  }, [location.pathname]);

  const userInfo = useSelector(userSelectors.makeSelectUserInfo());

  const isSuperAdmin = useCheckIsSuperAdmin();

  const isShowBillingReport =
    isSuperAdmin || Boolean(userInfo?.awgBillingReportEnable);

  if (isShowBillingReport) {
    return (
      <>
        <RibbonButton
          icon={<BillingReportIcon />}
          label='Billing Report'
          onClick={() => setVisible(true)}
        />

        {visible && (
          <BillingReportSection
            visible={visible}
            onCancel={() => setVisible(false)}
          />
        )}
      </>
    );
  }

  return null;
};

const BillingReportSection = (props) => {
  const [formInstance] = Form.useForm();

  const awgBillingMutation = useBillingReportActions();

  return (
    <StyledModal
      title='AWG Billing Report'
      width={720}
      okText='Export'
      onOk={async () => {
        const { billingPeriod, reportType } =
          await formInstance.validateFields();

        const startDate = formatRangeDateTime(billingPeriod?.[0]);
        const endDate = formatRangeDateTime(billingPeriod?.[1]);

        awgBillingMutation.mutate(
          {
            reportType,
            startDate,
            endDate,
          },
          {
            onSuccess: () => {
              props.onCancel();
            },
          }
        );
      }}
      okButtonProps={{ loading: awgBillingMutation.isLoading }}
      maskClosable={false}
      {...props}
    >
      <Form form={formInstance} labelCol={{ span: 6 }} layout='horizontal'>
        <Form.Item
          label={<Typography.Text strong>Report Type</Typography.Text>}
          name='reportType'
          rules={[{ required: true, message: 'Report Type is required' }]}
        >
          <Radio.Group>
            <Radio value='Daymon AWG Billing'>AWG Billing Detail</Radio>
            <Radio value='Daymon AR Form'>Daymon AR Form</Radio>
            <Radio value='AWG Billing Spreadsheet'>
              AWG Billing Spreadsheet
            </Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item dependencies={['reportType']} noStyle>
          {({ getFieldValue }) => {
            const reportType = getFieldValue('reportType');

            return (
              <Form.Item
                label={<Typography.Text strong>Billing Period</Typography.Text>}
                name='billingPeriod'
                rules={[
                  {
                    required: Boolean(reportType),
                    message: 'Billing period is required',
                  },
                ]}
              >
                <DatePicker.RangePicker
                  disabled={!reportType}
                  dropdownClassName='billing-report-dropdown'
                />
              </Form.Item>
            );
          }}
        </Form.Item>
      </Form>
    </StyledModal>
  );
};

export default BillingReportControl;
